import { useCallback, useRef } from 'react';
import { apiCall } from '../utils/api';
import { AdminAppState } from '../shared/types/system';

const REGISTRY_API_MAP: Partial<Record<keyof AdminAppState, string>> = {
	households: 'getHouseholds',
	members: 'getMembers',
	gifts: 'getAllGifts',
	tags: 'getTags',
	bags: 'getBags',
	inventory: 'listInventory',
	applications: 'getAllApplications',
	users: 'getUsers',
	venues: 'getVenues',
	events: 'getEvents',
	slots: 'getSlots',
	donors: 'getDonors',
	transactions: 'getTransactions',
	categories: 'getCategoryRegistry',
	applicationFields: 'getFormFields',
	fulfillmentGroupingRules: 'getTagRules',
	campaigns: 'listCampaigns'
};

// ADR-109: Ops Hub override for the registries that carry PII — always the unconditionally
// redacted getOps* endpoint, regardless of caller role. Every other registry name falls
// through to REGISTRY_API_MAP unchanged in both modes.
const OPS_REGISTRY_API_MAP: Partial<Record<keyof AdminAppState, string>> = {
	households: 'getOpsHouseholds',
	members: 'getOpsMembers',
	bags: 'getOpsBags'
};

export function useRegistryHydration(
	state: AdminAppState | null,
	setState: React.Dispatch<React.SetStateAction<AdminAppState | null>>,
	viewingCampaignId: string | null = null,
	opsMode: boolean = false
): { hydrateRegistry: (name: keyof AdminAppState, force?: boolean) => Promise<unknown[] | undefined> } {
	const pendingHydrations = useRef<Set<string>>(new Set());

	const hydrateRegistry = useCallback(async (name: keyof AdminAppState, force: boolean = false) => {
		const apiKey = (opsMode && OPS_REGISTRY_API_MAP[name]) || REGISTRY_API_MAP[name];
		if (!apiKey) return;

		// Returns the fetched rows (DL-511) so a caller that forced a refresh can act on fresh data
		// immediately, without waiting for the setState below to re-render it. Undefined when skipped.
		if (!force && (state?.hydrated[name] || pendingHydrations.current.has(name))) return;

		try {
			pendingHydrations.current.add(name);
			// Must omit the arg entirely (not pass `undefined`) when there's no campaign in view —
			// JSON.stringify silently turns an `undefined` array element into `null`, and
			// RegistryDAL.readAll treats explicit `null` as "every campaign, unfiltered" rather
			// than "use the active campaign" (DL-301).
			const data = viewingCampaignId
				? await apiCall<any[]>(apiKey, viewingCampaignId)
				: await apiCall<any[]>(apiKey);
			setState(current => {
				if (!current) return current;
				return {
					...current,
					[name]: data,
					hydrated: { ...current.hydrated, [name]: true }
				};
			});
			return data;
		} catch (err) {
			console.error(`Failed to hydrate registry: ${String(name)}`, err);
			throw err;
		} finally {
			pendingHydrations.current.delete(name);
		}
	}, [state?.hydrated, setState, viewingCampaignId, opsMode]);

	return { hydrateRegistry };
}
